import React, { FC } from 'react';
import { observer } from 'mobx-react-lite';
import clone from 'lodash/clone';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { useStore } from 'hooks';
import {
  checkSelect,
  checkCurrentCounter,
} from '../../../../../../../../utils';
import { IObject } from 'types';
import { FiasSearchProps } from '../../../../../../../../types';
import { TooltipInner } from '../../../../../../../../../../tooltip';
import {
  Wrapper,
  ChevronIcon,
  EmptyIcon,
  Text,
  Counter,
  SelectButton,
  CheckIcon,
} from './option.styled';

type OptionProps = {
  item: IObject;
  level: number;
  parent: string;
  show: boolean;
  setShow: () => void;
} & Pick<FiasSearchProps, 'flat' | 'selectOnlyHouses' | 'disabledHouses'>;

export const Option: FC<OptionProps> = observer(
  ({
    item,
    show,
    setShow,
    level,
    flat,
    parent,
    selectOnlyHouses,
    disabledHouses,
  }) => {
    const { fias } = useStore();

    const handleChangeSelectedItem = (
      currentItem: IObject,
      dontOpen?: boolean
    ): void => {
      // TODO 516994
      // Нижний уровень должен знать своего родителя
      if (
        !currentItem.inheritors &&
        !currentItem.houses &&
        !currentItem.parent
      ) {
        currentItem.parent = parent;
      }

      if (selectOnlyHouses) {
        // К выбору доступны только дома
        if (!flat) {
          if (currentItem.houses && !item.title) {
            if (fias.lastSelectedHouse === currentItem) {
              fias.setLastSelectedHouse({});
              fias.setShowSecondOptionsField(false);
            } else {
              fias.setLastSelectedHouse(currentItem);
              fias.setShowSecondOptionsField(true);
            }
          }

          currentItem.inheritors && setShow();
        } else {
          setShow();

          if (!currentItem.houses && !currentItem.inheritors) {
            let params: IObject[] = clone(fias.currentSelectedHouses);

            if (checkSelect(currentItem, params)) {
              params = params.filter(
                (item: IObject): boolean => item._id !== currentItem._id
              );
              fias.setShowSecondOptionsField(false);
            } else {
              fias.setShowSecondOptionsField(true);
              params.push(currentItem);
            }

            fias.setLastSelectedHouse(currentItem);
            fias.setCurrentSelectedHouses(params);

            // Запрос количества квартир при клике на дом
            flat && currentItem._id && fias.getFlatCount(currentItem._id);
          }
        }
      } else {
        // Можно выбрать всё что угодно
        let params: IObject[] = clone(fias.currentSelectedHouses);

        if (checkSelect(currentItem, params)) {
          params = params.filter(
            (item: IObject): boolean => item !== currentItem
          );
          fias.setShowSecondOptionsField(false);
          fias.setCurrentSelectedHouses(params);

          if (currentItem.inheritors || (flat && currentItem.houses)) {
            handleClearAll(currentItem);
          }
        } else {
          show && setShow();

          params.push(currentItem);
          fias.setCurrentSelectedHouses(params);

          if (item.inheritors || (flat && item.houses)) {
            handleClearAll(currentItem);
          }
        }

        // Запрос количества квартир при клике на дом
        if (flat && currentItem._id) {
          fias.getFlatCount(currentItem._id);
        }

        fias.setLastSelectedHouse(currentItem);

        if (!dontOpen) {
          if ((item.houses && !flat) || item._id) {
            fias.setShowSecondOptionsField(
              checkSelect(currentItem, fias.currentSelectedHouses)
            );
          } else {
            fias.setShowSecondOptionsField(false);
          }
        }
      }
    };

    const checkCurrentGroupDisabled = (currentItem: any) => {
      const itemId = currentItem.id || currentItem._id;
      return itemId
        ? disabledHouses.find((house) => house.id === itemId)
        : undefined;
    };

    const handleChangeSelectedGroups = (currentItem: IObject): void => {
      let params: IObject[] = clone(fias.currentSelectedHouses);

      if (checkCurrentGroupDisabled(currentItem)) {
        return;
      }

      if (checkSelect(currentItem, params)) {
        params = params.filter(
          (item: IObject): boolean => item.id !== currentItem.id
        );
      } else {
        params.push(currentItem);
      }

      fias.setCurrentSelectedHouses(params);
    };

    const handleSelectAll = (currentItem: IObject): void => {
      const params: IObject[] = clone(fias.currentSelectedHouses);
      const newParams: IObject[] = [];

      const collectHousesFromTree = (item: IObject, parentAOGUID?: string) => {
        // Если есть houses, добавляем их в newParams
        if (item.houses) {
          newParams.push(
            ...item.houses
              .filter((house: IObject): boolean => !checkSelect(house, params))
              .map((house: IObject) => ({
                ...house,
                parent: item.AOGUID || parentAOGUID,
              }))
          );
        }

        // Рекурсивный вызов для inheritors
        if (item.inheritors) {
          item.inheritors.forEach((inheritor: IObject) => {
            collectHousesFromTree(inheritor, item.AOGUID);
          });
        }
      };

      collectHousesFromTree(currentItem);

      params.push(
        ...newParams.flat(Infinity).map((el: IObject) => ({
          ...el,
          parent: el.parent || currentItem.AOGUID,
        }))
      );
      fias.setCurrentSelectedHouses(params);

      // Добавление самого currentItem в случае его отсутствия
      // if (!selectOnlyHouses && !checkSelect(currentItem, params)) {
      //   handleChangeSelectedItem(currentItem, true);
      // }
    };

    const handleClearAll = (currentItem: IObject): void => {
      const params: IObject[] = clone(fias.currentSelectedHouses);
      const newParams: IObject[] = params.filter(
        (item: IObject): boolean =>
          !checkSelect(item, currentItem.houses || currentItem.inheritors)
      );

      fias.setCurrentSelectedHouses(newParams);
      fias.setLastSelectedHouse({});

      // Удаление самого currentItem в случае его присутствия
      if (selectOnlyHouses && checkSelect(currentItem, params)) {
        handleChangeSelectedItem(currentItem, true);
      }

      fias.setShowSecondOptionsField(false);

      if (currentItem.inheritors) {
        currentItem.inheritors.forEach((item: IObject): void =>
          handleClearAll(item)
        );
      }
    };

    // проверяет, есть ли среди всех домов (из houses и вложенных inheritors) хотя бы один disabledHouse
    const hasDisabledHouses = (currentItem: IObject): boolean => {
      let disabledFound = false;

      const traverse = (item: IObject) => {
        const hasDisabled = item.houses?.some((house: IObject) => {
          const houseId = house.id || house._id;
          return disabledHouses.some((dis) => dis.id === houseId);
        });

        if (hasDisabled) {
          disabledFound = true;
        }

        item.inheritors?.forEach(traverse);
      };

      traverse(currentItem);
      return disabledFound;
    };

    const SelectButtonsBlock = (): JSX.Element | null => {
      // если есть хоть один disabled дом — не показываем кнопку вообще
      if (hasDisabledHouses(item)) {
        return null;
      }

      if (!checkCurrentCounter(item, fias.currentSelectedHouses)) {
        return (
          <SelectButton onClick={() => handleSelectAll(item)}>
            Выбрать всё
          </SelectButton>
        );
      }

      return (
        <SelectButton onClick={() => handleClearAll(item)}>
          Очистить
        </SelectButton>
      );
    };

    const isSelected: boolean = checkSelect(item, fias.currentSelectedHouses);

    return (
      <>
        <Wrapper
          isSelected={
            selectOnlyHouses
              ? fias.lastSelectedHouse === item && fias.showSecondOptionsField
              : isSelected
          }
          $isDisabled={!!checkCurrentGroupDisabled(item)}
          level={level}
          data-tooltip-id={item?.id}
          data-tooltip-variant={'info'}
        >
          {(item.inheritors || item.houses) && !item.title ? (
            // Опции в меню домов
            <>
              {item.inheritors || (item.houses && flat) ? (
                isSelected ? (
                  <EmptyIcon />
                ) : (
                  <ChevronIcon isOpen={show} onClick={() => setShow()} />
                )
              ) : (
                level === 1 && <EmptyIcon />
              )}
              <Text onClick={() => handleChangeSelectedItem(item)}>
                {item.name}
              </Text>
              {item.houses && (selectOnlyHouses || !isSelected) && (
                <>
                  <Counter>
                    {checkCurrentCounter(item, fias.currentSelectedHouses)}/
                    {item.houses?.length}
                  </Counter>
                  <SelectButtonsBlock />
                </>
              )}
            </>
          ) : (
            // Опции в меню групп и опции последнего уровня вложенности
            <>
              {(item.title || level === 1) && <EmptyIcon />}
              {item.title ? (
                <Text onClick={() => handleChangeSelectedGroups(item)}>
                  {item.title}
                </Text>
              ) : (
                <Text onClick={() => handleChangeSelectedItem(item)}>
                  {item.name || item.short_address}
                </Text>
              )}
              {isSelected && selectOnlyHouses && <CheckIcon />}
            </>
          )}
        </Wrapper>
        {!!checkCurrentGroupDisabled(item) && (
          <ReactTooltip id={item?.id} variant={'info'}>
            <TooltipInner>
              {checkCurrentGroupDisabled(item)!.reason}
            </TooltipInner>
          </ReactTooltip>
        )}
      </>
    );
  }
);
